import { useState } from "react";
import config from "config/config";
import { v4 as uuidv4 } from "uuid";
import { sha384 } from "crypto-hash";
import { Link } from "react-router-dom";
import Countdown from "react-countdown";
import { useForm } from "react-hook-form";
import { isAgentPortal } from "js/utilities";
import { useTranslation } from "react-i18next";
import FormInputError from "components/General/FormInputError";
import { resetPassword } from "service/userservice/resetPassword";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import DomainErrorModal from "components/ModalDialogs/DomainErrorModal";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import { sendEmailNotification } from "service/notificationservice/sendEmailNotification";

const ResetFirstTimePassword = () => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const [openEyePassword, setOpenEyePassword] = useState(true);
  const [openEyeConfirmPassword, setOpenEyeConfirmPassword] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [confirmationPassCode, setConfirmationPassCode] = useState("");
  const [errorInfo, seterrorInfo] = useState("");
  const [modalState, setModalState] = useState(false);
  const [currentCounterTime, setCurrentCounterTime] = useState(null);
  const [passcodeExpired, setPasscodeExpired] = useState(false);
  const [currentCounterIndex, setCurrentCounterIndex] = useState(0);

  const togglePasswordEye = () => {
    setOpenEyePassword(!openEyePassword);
  };

  const toggleConfirmPasswordEye = () => {
    setOpenEyeConfirmPassword(!openEyeConfirmPassword);
  };

  function randomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  const renderer = ({ minutes, seconds, completed }) => {
    if (completed) {
      setPasscodeExpired(true);
      return (
        <Link
          to=""
          className="mb-4 mr-5"
          onClick={async () => {
            let passcode = randomNumberInRange(100000, 999999);
            setConfirmationPassCode(passcode.toString());
            setLoading(true);
            let securitycode = randomNumberInRange(100000, 999999);
            let sec =
              parseInt(securitycode.toString().substring(3, 4)) === 0
                ? 1
                : parseInt(securitycode.toString().substring(3, 4));
            let confirmationcode = passcode * sec;

            let contentDictionary = {};
            contentDictionary.confirmationcode = confirmationcode.toString();
            contentDictionary.username =
              window.localStorage.getItem("firstLoginUserName");
            contentDictionary.securitycode = securitycode.toString();

            isAgentPortal()
              ? await sendEmailNotification(
                  window.localStorage.getItem("firstLoginUserEmail"),
                  12,
                  contentDictionary
                ).catch((error) => {
                  console.log(error);
                })
              : await sendEmailNotification(
                  window.localStorage.getItem("firstLoginUserEmail"),
                  2,
                  contentDictionary
                ).catch((error) => {
                  console.log(error);
                });

            setLoading(false);
            setCurrentCounterTime(
              Date.now() + parseInt(config.OTP_EXPIRY_TIME)
            );
            setCurrentCounterIndex(currentCounterIndex + 1);
            setPasscodeExpired(false);
          }}
        >
          Resend Code
        </Link>
      );
    } else {
      return (
        <p className="text-sm">
          Code expires in {minutes > 9 ? minutes : "0" + minutes}:
          {seconds > 9 ? seconds : "0" + seconds}
        </p>
      );
    }
  };

  async function onSubmit(data) {
    if (currentStep === 1) {
      let passcode = randomNumberInRange(100000, 999999);
      setConfirmationPassCode(passcode.toString());
      setLoading(true);
      let securitycode = randomNumberInRange(100000, 999999);
      let sec =
        parseInt(securitycode.toString().substring(3, 4)) === 0
          ? 1
          : parseInt(securitycode.toString().substring(3, 4));
      let confirmationcode = passcode * sec;

      let contentDictionary = {};
      contentDictionary.confirmationcode = confirmationcode.toString();
      contentDictionary.username =
        window.localStorage.getItem("firstLoginUserName");
      contentDictionary.securitycode = securitycode.toString();

      isAgentPortal()
        ? await sendEmailNotification(
            window.localStorage.getItem("firstLoginUserEmail"),
            12,
            contentDictionary
          ).catch((error) => {
            console.log(error);
          })
        : await sendEmailNotification(
            window.localStorage.getItem("firstLoginUserEmail"),
            2,
            contentDictionary
          ).catch((error) => {
            console.log(error);
          });
      setLoading(false);
      setCurrentStep(currentStep + 1);
      setCurrentCounterTime(Date.now() + parseInt(config.OTP_EXPIRY_TIME));
    } else if (currentStep === 2) {
      if (passcodeExpired) {
        seterrorInfo(t("ResetPassword-error-3"));
        setModalState(true);
      } else {
        if (
          data.resetCode === confirmationPassCode ||
          (config.ENABLE_DEFAULT_OTP === "true" &&
            data.resetCode === config.DEFAULT_OTP)
        ) {
          setLoading(true);
          await resetPassword(
            parseInt(window.localStorage.getItem("firstLoginUserId")),
            (await sha384(data.password)).toString().substring(15, 30)
          )
            .then((data) => {
              setLoading(false);
              setCurrentStep(currentStep + 1);
              localStorage.removeItem("firstLoginUserId");
              localStorage.removeItem("firstLoginUserEmail");
              localStorage.removeItem("firstLoginUserName");
            })
            .catch((error) => {
              if (error.status && error.status === 403) {
                setLoading(false);
                seterrorInfo(t("ResetPassword-error-5"));
                setModalState(true);
              } else {
                setLoading(false);
                seterrorInfo(t("ResetPassword-error-6"));
                setModalState(true);
                console.log(error);
              }
            });
        } else {
          seterrorInfo(t("ResetPassword-error-4"));
          setModalState(true);
        }
      }
    }
  }

  const uuid = uuidv4();

  return (
    <EverlakeLoadingOverlay isLoading={loading}>
      {currentStep === 2 && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="container ui-component-spacing">
            <div className="ui-card p-4 mx-auto sm:w-2/3 md:w-3/5 lg:w-1/2 md:p-6">
              <h2 className="font-body font-bold text-xl">
                {t("ResetPassword-firstTime-form-title")}
              </h2>
              <p className="text-sm">
                {t("ResetPassword-firstTime-form-step-2-subtitle", {
                  minute: parseInt(parseInt(config.OTP_EXPIRY_TIME) / 60000),
                })}
              </p>
              <div className="mt-4 md:mt-6">
                <label
                  id="ID_passwordResetCodeLabel"
                  htmlFor="ID_passwordResetCode"
                >
                  {t("ResetPassword-firstTime-form-resetCode")}
                </label>
                <input
                  id="ID_passwordResetCode"
                  aria-labelledby="ID_passwordResetCodeLabel"
                  aria-describedby="ID_passwordResetCodeError"
                  type="text"
                  autoComplete="off"
                  {...register("resetCode", {
                    required: {
                      value: true,
                      message: t("ResetPassword-error-9"),
                    },
                    pattern: {
                      value: /^\d{6}$/,
                      message: t("ResetPassword-error-10"),
                    },
                  })}
                />
                {errors.resetCode && (
                  <FormInputError
                    id={`${uuid}-resetCode-error`}
                    errorMessage={errors.resetCode.message}
                  />
                )}
              </div>
              <div className="mt-4 md:mt-6">
                <Countdown
                  date={currentCounterTime}
                  key={currentCounterIndex}
                  renderer={renderer}
                />
              </div>
              <div className="text-center mt-4 md:mt-6">
                <button className="ui-btn-primary ui-btn-min-w">
                  {t("ResetPassword-firstTime-form-continue-button")}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {currentStep === 1 && (
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Forgot password - Reset your password - SPRINT-1-CUSTOMER-my-policies-landing-and-unauthenticated-landing.pdf Pg 26 */}
          <div className="container ui-component-spacing">
            <div className="ui-card p-4 mx-auto sm:w-2/3 md:w-3/5 lg:w-1/2 md:p-6">
              <h2 className="font-body font-bold text-xl">
                {t("ResetPassword-firstTime-form-title")}
              </h2>
              <p className="text-sm">
                {t("ResetPassword-firstTime-form-subtitle")}
              </p>
              <ul className="list-disc ml-4">
                <li>{t("ResetPassword-firstTime-form-helpText-1")}</li>
                <li>{t("ResetPassword-firstTime-form-helpText-2")}</li>
                <li>{t("ResetPassword-firstTime-form-helpText-3")}</li>
                <li>{t("ResetPassword-firstTime-form-helpText-4")}</li>
                <li>{t("ResetPassword-firstTime-form-helpText-5")}</li>
              </ul>
              <div className="mt-4 md:mt-6 relative">
                <label id="ID_passwordResetLabel" htmlFor="ID_passwordReset">
                  {t("ResetPassword-firstTime-form-newPassword")}
                </label>
                <input
                  id="ID_passwordReset"
                  aria-labelledby="ID_passwordResetLabel"
                  aria-describedby="ID_passwordResetError"
                  type={openEyePassword ? "password" : "text"}
                  autoComplete="off"
                  {...register("password", {
                    required: t("ResetPassword-error-12"),
                    validate: {
                      minimumCharCount: (v) =>
                        v.length >= 10 || t("ResetPassword-error-12"),
                      maximumCharCount: (v) =>
                        v.length <= 60 || t("ResetPassword-error-12"),
                      hasLowerCaseLetter: (v) =>
                        /^(?=.*[a-z])/.test(v) || t("ResetPassword-error-13"),
                      hasCapitalizedCaseLetter: (v) =>
                        /^(?=.*[A-Z])/.test(v) || t("ResetPassword-error-14"),
                      hasNumericLetter: (v) =>
                        /^(?=.*[0-9])/.test(v) || t("ResetPassword-error-15"),
                      allowedSpecialChars: (v) =>
                        /^(?=.*[!@#\$%&\*\(\)])/.test(v) ||
                        t("ResetPassword-error-16"),
                      illegalLetter: (v) =>
                        v.replace(/[A-Za-z0-9!@#\$%&\*\(\)]/g, "").length < 1 ||
                        t("ResetPassword-error-17"),
                    },
                  })}
                />
                <div className="absolute top-8 right-4">
                  {openEyePassword ? (
                    <AiOutlineEye onClick={togglePasswordEye}></AiOutlineEye>
                  ) : (
                    <AiOutlineEyeInvisible
                      onClick={togglePasswordEye}
                    ></AiOutlineEyeInvisible>
                  )}
                </div>
                {errors.password && (
                  <FormInputError
                    id={`${uuid}-password-error`}
                    errorMessage={errors.password.message}
                  />
                )}
              </div>
              <div className="mt-4 md:mt-6 relative">
                <label
                  id="ID_passwordResetConfirmLabel"
                  htmlFor="ID_passwordResetConfirm"
                >
                  {t("ResetPassword-firstTime-form-confirmPassword")}
                </label>
                <input
                  id="ID_passwordResetConfirm"
                  aria-labelledby="ID_passwordResetConfirmLabel"
                  aria-describedby="ID_passwordResetConfirmError"
                  type={openEyeConfirmPassword ? "password" : "text"}
                  autoComplete="off"
                  {...register("confirmPassword", {
                    validate: (value) => {
                      if (watch("password") !== value) {
                        return "The passwords do not match";
                      }
                    },
                  })}
                />
                <div className="absolute top-8 right-4">
                  {openEyeConfirmPassword ? (
                    <AiOutlineEye
                      onClick={toggleConfirmPasswordEye}
                    ></AiOutlineEye>
                  ) : (
                    <AiOutlineEyeInvisible
                      onClick={toggleConfirmPasswordEye}
                    ></AiOutlineEyeInvisible>
                  )}
                </div>
                {errors.confirmPassword && (
                  <FormInputError
                    id={`${uuid}-confirmPassword-error`}
                    errorMessage={errors.confirmPassword.message}
                  />
                )}
              </div>
              <div className="text-center mt-4 md:mt-6">
                <button className="ui-btn-primary ui-btn-min-w">
                  {t("ResetPassword-firstTime-form-submit-button")}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {currentStep === 3 && (
        <div className="container ui-component-spacing">
          <div className="ui-card p-4 mx-auto sm:w-2/3 md:w-3/5 lg:w-1/2 md:p-6">
            <h2 className="font-body font-bold text-xl">
              {t("ResetPassword-firstTime-form-step-3-title")}
            </h2>
            <p className="text-sm">
              {t("ResetPassword-firstTime-form-step-3-subtitle")}
            </p>
            <div className="text-xl text-center font-semibold mt-4 md:mt-6">
              {t("ResetPassword-firstTime-form-step-3-thankYou")}
            </div>
            <div className="text-center mt-4 md:mt-6">
              <Link to="/" className="ui-btn-primary ui-btn-min-w">
                {t("ResetPassword-firstTime-form-step-3-returnToLogin-button")}
              </Link>
            </div>
          </div>
        </div>
      )}
      <DomainErrorModal
        modalState={modalState}
        setModalState={setModalState}
        errorInfo={errorInfo}
      />
    </EverlakeLoadingOverlay>
  );
};

export default ResetFirstTimePassword;
